import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Home, GraduationCap, Wallet, MapPin } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { F, HERO, PayBadge, Devices } from "@/components/careers/ui";

const WHY = [[Home, "Work from home", "No commute. Call from anywhere in the US."], [GraduationCap, "Paid-for training", "Our Sales Training Academy gets you call-ready."], [Wallet, "Weekly commissions", "Hourly and commission-only roles available."]];

const JobCard = ({ j, i }) => (
  <Link to={`/careers/${j.slug}`} className="group block bg-white border border-slate-200 hover:border-slate-900 rounded-2xl p-6 transition-colors animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${i * 70}ms`, animationFillMode: "both" }} data-testid={`job-card-${j.slug}`}>
    <div className="flex flex-wrap items-start gap-3">
      <div className="flex-1 min-w-[220px] space-y-2">
        <PayBadge j={j} />
        <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight" style={F}>{j.title}</h3>
        <p className="text-sm text-slate-600 max-w-2xl">{j.summary}</p>
      </div>
      <ArrowRight className="w-6 h-6 text-slate-300 group-hover:text-slate-900 group-hover:translate-x-1 transition-transform" />
    </div>
    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
      <span className="font-bold text-slate-900 text-sm">{j.pay_text}</span>
      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{j.hours}</span>
      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.location}</span>
      <Devices list={j.devices} />
    </div>
  </Link>
);

export default function CareersPage() {
  usePageMeta("Work From Home Sales Jobs", "Remote phone sales jobs at ShopAdvisor. Use your computer, phone or tablet. Hourly and commission roles.");
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  useEffect(() => { api.get("/api/careers/jobs").then(r => setJobs(r.data)).catch(() => setJobs([])); }, []);
  return (
    <div className="min-h-screen bg-[#f6f5f1]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="careers-page">
      <section className="relative bg-slate-950 text-white pt-28 pb-20 overflow-hidden">
        <img src={HERO} alt="Remote sales rep working from home" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 space-y-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber-400">Careers · 100% remote</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl" style={F}>Make calls from home. Get paid to help local shops grow.</h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl">We're hiring phone reps across the US. All you need is your computer, phone or tablet and a quiet place to talk.</p>
          <div className="flex flex-wrap gap-3">
            <a href="#jobs" className="h-12 px-6 leading-[3rem] rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold transition-colors" data-testid="careers-see-jobs">See open jobs</a>
            {user?.role === "applicant" && <Link to="/careers/portal" className="h-12 px-6 leading-[3rem] rounded-full border border-white/30 hover:bg-white/10 font-semibold transition-colors" data-testid="careers-portal-link">My applications</Link>}
            {!user && <Link to="/login?next=/careers/portal" className="h-12 px-6 leading-[3rem] rounded-full border border-white/30 hover:bg-white/10 font-semibold transition-colors" data-testid="careers-signin-link">Applicant sign in</Link>}
          </div>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-4 -mt-8 relative grid md:grid-cols-3 gap-3">
        {WHY.map(([I, t, d]) => <div key={t} className="bg-white border border-slate-200 rounded-2xl p-5"><I className="w-6 h-6 text-amber-600" /><p className="mt-2 font-extrabold text-slate-900" style={F}>{t}</p><p className="text-sm text-slate-600">{d}</p></div>)}
      </section>
      <section id="jobs" className="max-w-6xl mx-auto px-4 py-16 space-y-4" data-testid="careers-jobs">
        <h2 className="text-3xl font-extrabold text-slate-900" style={F}>Open positions {jobs && <span className="text-slate-400">({jobs.length})</span>}</h2>
        {!jobs ? <p className="text-slate-400">Loading…</p> : jobs.length ? jobs.map((j, i) => <JobCard key={j.id} j={j} i={i} />) : <p className="text-slate-500" data-testid="careers-empty">No open positions right now — check back soon.</p>}
      </section>
    </div>
  );
}
